import { clsx } from "clsx";
import { useState } from "react";
import { useTranslations } from "translations";
import { StepItem } from "./step-item";
import type { Step } from "./types";

const EMPTY_STEPS = Object.freeze({}) as Record<number, Step>;

function getStepArray<T extends number>(
  steps: Record<T, Step>,
): { step: Step; stepIndex: T }[] {
  return Object.entries(steps).map(([positionString, step]) => ({
    step,
    stepIndex: Number(positionString) as T,
  })) as { step: Step; stepIndex: T }[];
}

export type StepperProps<T extends number> = {
  className?: string;
  currentStep?: T;
  initialStep?: T;
  navAriaLabel?: string;
  onStepChange?: (stepIndex: T) => void;
  stepClassName?: string;
  steps: Record<T, Step>;
};

export function Stepper<T extends number>({
  className,
  currentStep: currentStepProp,
  initialStep,
  navAriaLabel,
  onStepChange,
  stepClassName,
  steps = EMPTY_STEPS as Record<T, Step>,
}: StepperProps<T>) {
  const translations = useTranslations();
  const stepArray = getStepArray(steps);

  const [internalStep, setInternalStep] = useState<T>(
    initialStep ?? stepArray[0]?.stepIndex ?? (0 as T),
  );

  if (!stepArray.length) return null;

  const currentStep = currentStepProp ?? internalStep;

  const handleStepChange = (stepIndex: T) => {
    if (currentStepProp === undefined) setInternalStep(stepIndex);
    onStepChange?.(stepIndex);
  };

  const isInteractive = typeof onStepChange === "function";
  const ariaLabel = translations.accessibility.stepperStepOfAriaLabel({
    currentStep: (currentStep + 1).toString(),
    totalSteps: stepArray.length.toString(),
  });

  return (
    <nav
      aria-label={navAriaLabel}
      className="-my-4 max-w-fit overflow-x-auto px-2 py-4"
    >
      <ol
        aria-label={ariaLabel}
        aria-live="polite"
        className={clsx("flex flex-row flex-nowrap gap-x-3", className)}
      >
        {stepArray.map(({ step, stepIndex }, index) => (
          <StepItem
            key={stepIndex}
            currentStep={currentStep}
            isInteractive={isInteractive}
            isLast={index === stepArray.length - 1}
            step={step}
            stepClassName={stepClassName}
            stepIndex={stepIndex}
            onStepChange={handleStepChange}
          />
        ))}
      </ol>
    </nav>
  );
}

Stepper.displayName = "RecareUI.Stepper";
